import useDB from "../../hooks/useDB";
import Loading from "../../components/Loading";
import Error from "../../components/Error";
import { useParams } from "react-router-dom";
import HtTabs from "../../components/HtTabs";
import LessonContent from "./LessonContent";
import Feedback2 from "./Feedback2";
import { Book, BookOpen, ExternalLink } from "lucide-react";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * LessonPlanning - Main lesson planning page showing lesson content and feedback tabs
 * @param {object} props
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 3.0.0
 * @since 2026-04-14
 */
export default function LessonPlanning(props) {
  const { primaryColor } = usePrimaryColor();
  const { lessonId } = useParams();
  const [lesson, setLesson, error, loading] = useDB(`/lesson2/${lessonId}`);

  if (loading) return <Loading />;
  if (error) return <Error />;
  const { cLesson } = lesson;
  const books = cLesson?.syllabus?.books || lesson.books || [];
  const isTest = !cLesson && lesson.unit;

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <h2 className="text-2xl font-light text-slate-800 tracking-tight">
          {lesson.classId.name}{" "}
          <span className="text-slate-400">—</span>{" "}
          <span className="text-slate-500 text-lg">{new Date(lesson.tDate).fDate()}</span>
        </h2>
      </div>

      {(cLesson || isTest) && (
        <div className="space-y-6">
          {/* Lesson info card */}
          <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm mb-1">
              <span className="font-semibold text-slate-800 text-base">{lesson.syllabusName}</span>
              <span className="text-slate-300">·</span>
              {cLesson ? (
                <>
                  <span className="text-slate-500">
                    Unit {cLesson.unit.unitNumber}: {cLesson.unit.name}
                  </span>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500">
                    Lesson {cLesson.lessonNumber}: {cLesson.name}
                  </span>
                </>
              ) : (
                <span className="text-slate-500">
                  Unit {lesson.unit.unitNumber}: {lesson.unit.name}
                </span>
              )}
            </div>
          </div>

          {/* Course Books */}
          {books.length > 0 && (
            <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Book size={20} className={`text-${primaryColor}-800`} />
                Course Books
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {books.map((b, i) => (
                  <a
                    key={b._id || i}
                    href={b.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 p-3 bg-slate-50 hover:bg-${primaryColor}-50 border border-slate-200 hover:border-${primaryColor}-800/20 rounded-[10px] transition-all duration-150 group cursor-pointer`}
                  >
                    <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                      <BookOpen size={18} className={`text-${primaryColor}-800`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 text-sm truncate">{b.name || "Untitled Book"}</div>
                      <div className="text-xs text-slate-400">Read Online</div>
                    </div>
                    <ExternalLink size={14} className={`text-slate-400 group-hover:text-${primaryColor}-800`} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <HtTabs
        labels={["Content", "Feedback"]}
        components={[
          <LessonContent
            cLesson={cLesson}
            lesson={lesson}
            setLesson={setLesson}
          />,
          <Feedback2
            lesson={lesson}
            setLesson={setLesson}
          />,
        ]}
      />
    </div>
  );
}
